import { GiftCard, GiftCardDisplayStatus } from '@/types';
import { formatPriceIDR } from './format-utils';
import type { BadgeTone } from '@/components/ui/Badge';

/**
 * Today in Bali (UTC+8). Cards "expire" at the end of expiry_date Bali-local —
 * a card whose expiry_date is today is still valid; tomorrow it's not.
 *
 * Mirrors the (now() AT TIME ZONE 'Asia/Makassar')::date expression used in
 * the redeem_gift_card_and_create_membership RPC. Keeping the same definition
 * here means the UI's "expired" badge and the server's redemption rejection
 * agree on when a card flips.
 */
export function getBaliToday(referenceDate?: Date): Date {
  const now = referenceDate ?? new Date();
  // Bali offset = +8h. Shift the timestamp into Bali wall-clock, then floor to date.
  const baliMs = now.getTime() + 8 * 60 * 60 * 1000;
  const bali = new Date(baliMs);
  return new Date(Date.UTC(bali.getUTCFullYear(), bali.getUTCMonth(), bali.getUTCDate()));
}

/**
 * Derive the display status from a gift card's DB status, balance, and expiry.
 *
 * Order of precedence: cancelled → fully_redeemed → expired → active.
 * (Cancellation is the strongest signal; a $0 balance is "fully redeemed"
 *  even if technically still in its expiry window.)
 */
export function getGiftCardDisplayStatus(
  card: Pick<GiftCard, 'status' | 'balance_remaining' | 'expiry_date'>,
  referenceDate?: Date
): GiftCardDisplayStatus {
  if (card.status === 'cancelled') return 'cancelled';
  if (card.balance_remaining <= 0) return 'fully_redeemed';

  if (card.expiry_date) {
    const today = getBaliToday(referenceDate);
    const expiry = new Date(card.expiry_date + 'T00:00:00Z');
    if (expiry.getTime() < today.getTime()) return 'expired';
  }

  return 'active';
}

/** Whether this card can currently be redeemed against a new membership. */
export function isGiftCardRedeemable(
  card: Pick<GiftCard, 'status' | 'balance_remaining' | 'expiry_date'>,
  referenceDate?: Date
): boolean {
  return getGiftCardDisplayStatus(card, referenceDate) === 'active';
}

/** Badge tone for the StatusBadge — matches the membership badge palette. */
export function getGiftCardStatusTone(status: GiftCardDisplayStatus): BadgeTone {
  switch (status) {
    case 'active':         return 'green';
    case 'fully_redeemed': return 'slate';
    case 'expired':        return 'amber';
    case 'cancelled':      return 'red';
    default:               return 'gray';
  }
}

const STATUS_LABELS: Record<GiftCardDisplayStatus, string> = {
  active: 'Active',
  fully_redeemed: 'Fully Redeemed',
  expired: 'Expired',
  cancelled: 'Cancelled',
};

export function formatGiftCardStatus(status: GiftCardDisplayStatus): string {
  return STATUS_LABELS[status];
}

/** Format an IDR amount for display. Alias of {@link formatPriceIDR}. */
export const formatIDR = formatPriceIDR;
